import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { authApi, tenantApi, setApiBaseUrl } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { loadLoginPreferences, readCachedTenantForIdentifier } from '../services/loginPreferences';
import type { TenantResolveResponse } from '../services/api';
import { getProductionTenantIdentifier, isLocalDevHost } from '../utils/tenantHost';
import type { MfaLoginChallenge, MfaMethod } from '../types';

type ForgotPasswordStep = 'email' | 'question' | 'success';
type AuthStep = 'company' | 'credentials' | 'mfa';

const MFA_METHOD_LABELS: Record<MfaMethod, string> = {
  Authenticator: 'Authenticator app',
  Email: 'Email',
  Sms: 'SMS',
};

/** QR / setup-confirm when Authenticator is allowed but not yet the enrolled method. */
const needsAuthenticatorSetup = (challenge: MfaLoginChallenge, method: MfaMethod) =>
  method === 'Authenticator' &&
  (challenge.mfaSetupRequired || challenge.enrolledMethod !== 'Authenticator');

const getInitialPrefs = () => {
  try {
    return loadLoginPreferences();
  } catch {
    return { lastCompanyName: null, lastCompanyCode: null, lastEmail: null, rememberEmail: true };
  }
};

const Login: React.FC = () => {
  const initialPrefs = getInitialPrefs();
  const requiresCompanyStep = useMemo(
    () => isLocalDevHost(typeof window !== 'undefined' ? window.location.hostname : 'localhost'),
    [],
  );
  const initialCompany = initialPrefs.lastCompanyName || initialPrefs.lastCompanyCode || '';
  const initialIdentifier = requiresCompanyStep
    ? initialCompany
    : (getProductionTenantIdentifier() || initialCompany);
  const initialCachedTenant = initialIdentifier
    ? readCachedTenantForIdentifier(initialIdentifier)
    : null;

  const [step, setStep] = useState<AuthStep>(
    requiresCompanyStep && !initialCachedTenant ? 'company' : 'credentials'
  );
  const [companyName, setCompanyName] = useState(initialCompany);
  const [resolvedTenant, setResolvedTenant] = useState<TenantResolveResponse | null>(() => {
    if (initialCachedTenant?.apiUrl) setApiBaseUrl(initialCachedTenant.apiUrl);
    return initialCachedTenant;
  });
  const [email, setEmail] = useState(initialPrefs.rememberEmail && initialPrefs.lastEmail ? initialPrefs.lastEmail : '');
  const [password, setPassword] = useState('');
  const [rememberEmail, setRememberEmail] = useState(initialPrefs.rememberEmail);
  const [hasAutoResolved, setHasAutoResolved] = useState(() => !!initialCachedTenant);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordStep, setForgotPasswordStep] = useState<ForgotPasswordStep>('email');
  const [forgotEmail, setForgotEmail] = useState('');
  const [passwordQuestion, setPasswordQuestion] = useState('');
  const [passwordAnswer, setPasswordAnswer] = useState('');
  const [isLoadingForgotPassword, setIsLoadingForgotPassword] = useState(false);

  // MFA challenge state
  const [mfaChallenge, setMfaChallenge] = useState<MfaLoginChallenge | null>(null);
  const [mfaMethod, setMfaMethod] = useState<MfaMethod>('Authenticator');
  const [mfaCode, setMfaCode] = useState('');
  const [mfaCodeSent, setMfaCodeSent] = useState(false);
  const [mfaDestination, setMfaDestination] = useState<string | null>(null);
  const [authenticatorSecret, setAuthenticatorSecret] = useState<string | null>(null);
  const [authenticatorOtpauthUrl, setAuthenticatorOtpauthUrl] = useState<string | null>(null);
  const [mfaSetupStarted, setMfaSetupStarted] = useState(false);

  const { login, setSession } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (hasAutoResolved) return;

    const applyTenant = (tenant: TenantResolveResponse) => {
      if (!tenant.isActive) throw new Error('Tenant is inactive');
      if (!tenant.apiUrl?.trim()) throw new Error('Tenant has no ApiUrl configured in database.');
      setApiBaseUrl(tenant.apiUrl);
      setResolvedTenant(tenant);
      setStep('credentials');
    };

    const resolveTenant = (identifier: string) => {
      // Mark attempted immediately so a failed resolve does not re-trigger this effect in a loop.
      setHasAutoResolved(true);
      const cached = readCachedTenantForIdentifier(identifier);
      if (cached) {
        applyTenant(cached);
        void tenantApi.resolve(identifier.trim()).then(applyTenant).catch(() => undefined);
        return;
      }
      setIsLoading(true);
      tenantApi.resolve(identifier.trim())
        .then(applyTenant)
        .catch((error: any) => {
          const isNetwork =
            error?.code === 'ERR_NETWORK' ||
            error?.code === 'ECONNREFUSED' ||
            /network error|connection refused|failed to fetch/i.test(String(error?.message || ''));
          const msg = isNetwork
            ? 'Cannot reach tenant discovery service. Check that it is running, then try again.'
            : (error?.response?.data?.message || error?.message || 'Could not resolve company from this URL.');
          setErrorMessage(msg);
        })
        .finally(() => setIsLoading(false));
    };

    if (requiresCompanyStep) {
      if (!companyName.trim()) return;
      const prefs = loadLoginPreferences();
      const hasCompany = !!(prefs.lastCompanyName || prefs.lastCompanyCode);
      if (!hasCompany) return;
      resolveTenant(companyName);
      return;
    }

    const identifier = getProductionTenantIdentifier();
    if (!identifier) {
      setErrorMessage('Could not determine company from this URL.');
      setHasAutoResolved(true);
      return;
    }
    resolveTenant(identifier);
  }, [companyName, hasAutoResolved, requiresCompanyStep]);

  const resetMfaState = () => {
    setMfaChallenge(null);
    setMfaCode('');
    setMfaCodeSent(false);
    setMfaDestination(null);
    setAuthenticatorSecret(null);
    setAuthenticatorOtpauthUrl(null);
    setMfaSetupStarted(false);
  };

  const finishLoginSuccess = (employee: Parameters<typeof setSession>[0], token: string) => {
    if (!resolvedTenant) return;
    setSession(employee, token, {
      tenantId: resolvedTenant.tenantId,
      tenantCode: resolvedTenant.tenantCode,
      tenantName: resolvedTenant.tenantName,
      email: email.trim(),
      rememberEmail,
    });
    setEmail('');
    setPassword('');
    resetMfaState();
    const redirectPath = sessionStorage.getItem('loginRedirect') || '/';
    sessionStorage.removeItem('loginRedirect');
    navigate(redirectPath);
  };

  const beginMfaChallenge = async (challenge: MfaLoginChallenge) => {
    // After enrollment, always challenge with the enrolled method (not another allowed role method).
    const preferred =
      !challenge.mfaSetupRequired &&
      challenge.enrolledMethod &&
      challenge.allowedMethods.includes(challenge.enrolledMethod)
        ? challenge.enrolledMethod
        : challenge.defaultMethod && challenge.allowedMethods.includes(challenge.defaultMethod)
          ? challenge.defaultMethod
          : challenge.allowedMethods[0];
    setMfaChallenge(challenge);
    setMfaMethod(preferred);
    setMfaCode('');
    setMfaCodeSent(false);
    setMfaDestination(challenge.destinationHint ?? null);
    setAuthenticatorSecret(null);
    setAuthenticatorOtpauthUrl(null);
    setMfaSetupStarted(false);
    setStep('mfa');

    if (needsAuthenticatorSetup(challenge, preferred)) {
      try {
        const start = await authApi.mfa.setupAuthenticatorStart(challenge.mfaChallengeToken);
        setAuthenticatorSecret(start.secret);
        setAuthenticatorOtpauthUrl(start.otpauthUrl);
        setMfaSetupStarted(true);
      } catch (err: any) {
        setErrorMessage(err?.response?.data?.message || err?.message || 'Failed to start authenticator setup.');
      }
    }
  };

  const handleCompanyLookup = async (e?: React.FormEvent) => {
    e?.preventDefault();
    setErrorMessage('');
    if (!companyName.trim()) {
      setErrorMessage('Please enter your company name');
      return;
    }
    setHasAutoResolved(true);
    const cached = readCachedTenantForIdentifier(companyName);
    if (cached?.apiUrl) {
      setApiBaseUrl(cached.apiUrl);
      setResolvedTenant(cached);
      setStep('credentials');
      void tenantApi.resolve(companyName.trim()).then((tenant) => {
        if (tenant.isActive && tenant.apiUrl?.trim()) {
          setApiBaseUrl(tenant.apiUrl);
          setResolvedTenant(tenant);
        }
      }).catch(() => undefined);
      return;
    }
    setIsLoading(true);
    try {
      const tenant = await tenantApi.resolve(companyName.trim());
      if (!tenant.isActive) throw new Error('Tenant is inactive');
      if (!tenant.apiUrl?.trim()) throw new Error('Tenant has no ApiUrl configured in database.');
      setApiBaseUrl(tenant.apiUrl);
      setResolvedTenant(tenant);
      setStep('credentials');
    } catch (error: any) {
      const isNetwork =
        error?.code === 'ERR_NETWORK' ||
        error?.code === 'ECONNREFUSED' ||
        /network error|connection refused|failed to fetch/i.test(String(error?.message || ''));
      const msg = isNetwork
        ? 'Cannot reach tenant discovery service. Check that it is running, then try again.'
        : (error?.response?.data?.message || error?.message || 'Company not found. Please check the name and try again.');
      setErrorMessage(msg);
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackToCompany = () => {
    if (!requiresCompanyStep) return;
    setStep('company');
    setResolvedTenant(null);
    setCompanyName('');
    setErrorMessage('');
    setHasAutoResolved(true); // do not auto-retry resolve after going back
    resetMfaState();
  };

  const handleBackToCredentials = () => {
    setStep('credentials');
    setErrorMessage('');
    resetMfaState();
    setIsLoading(false);
  };

  const handleLogin = async (e?: React.FormEvent) => {
    e?.preventDefault();
    setErrorMessage('');
    if (!resolvedTenant) return;
    if (!email.trim()) {
      setErrorMessage('Please enter your email');
      return;
    }
    if (!password.trim()) {
      setErrorMessage('Please enter your password');
      return;
    }
    setIsLoading(true);
    try {
      const response = await login(resolvedTenant.tenantCode, email.trim(), password, {
        rememberEmail,
        resolvedTenant,
      });
      if ('mfaRequired' in response && response.mfaRequired) {
        await beginMfaChallenge(response);
        return;
      }
      setEmail('');
      setPassword('');
      const redirectPath = sessionStorage.getItem('loginRedirect') || '/';
      sessionStorage.removeItem('loginRedirect');
      navigate(redirectPath);
    } catch (error: any) {
      const errorMsg = error?.message || 'Invalid email or password. Please try again.';
      setErrorMessage(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  const handleMfaMethodChange = async (method: MfaMethod) => {
    if (!mfaChallenge) return;
    setMfaMethod(method);
    setMfaCode('');
    setMfaCodeSent(false);
    setMfaDestination(mfaChallenge.destinationHint ?? null);
    setErrorMessage('');

    if (needsAuthenticatorSetup(mfaChallenge, method) && (!mfaSetupStarted || !authenticatorOtpauthUrl)) {
      setIsLoading(true);
      try {
        const start = await authApi.mfa.setupAuthenticatorStart(mfaChallenge.mfaChallengeToken);
        setAuthenticatorSecret(start.secret);
        setAuthenticatorOtpauthUrl(start.otpauthUrl);
        setMfaSetupStarted(true);
      } catch (err: any) {
        setErrorMessage(err?.response?.data?.message || err?.message || 'Failed to start authenticator setup.');
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleSendMfaCode = async () => {
    if (!mfaChallenge) return;
    setErrorMessage('');
    setIsLoading(true);
    try {
      if (mfaChallenge.mfaSetupRequired) {
        // For setup of Email/SMS, send-code still uses challenge token
        const result = await authApi.mfa.sendCode(mfaChallenge.mfaChallengeToken, mfaMethod);
        setMfaCodeSent(true);
        setMfaDestination(result.destination ?? mfaChallenge.destinationHint ?? null);
      } else {
        const result = await authApi.mfa.sendCode(mfaChallenge.mfaChallengeToken, mfaMethod);
        setMfaCodeSent(true);
        setMfaDestination(result.destination ?? mfaChallenge.destinationHint ?? null);
      }
    } catch (err: any) {
      setErrorMessage(err?.response?.data?.message || err?.message || 'Failed to send code.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyMfa = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!mfaChallenge || !resolvedTenant) return;
    if (!mfaCode.trim()) {
      setErrorMessage('Please enter the verification code');
      return;
    }
    setErrorMessage('');
    setIsLoading(true);
    try {
      let result;
      const useSetupConfirm =
        mfaChallenge.mfaSetupRequired || needsAuthenticatorSetup(mfaChallenge, mfaMethod);
      if (useSetupConfirm) {
        result = await authApi.mfa.setupConfirm(mfaChallenge.mfaChallengeToken, mfaMethod, mfaCode.trim());
      } else {
        result = await authApi.mfa.verify(mfaChallenge.mfaChallengeToken, mfaCode.trim(), mfaMethod);
      }
      if (!result.token || !result.employee) {
        throw new Error('MFA verification failed');
      }
      finishLoginSuccess(result.employee, result.token);
    } catch (err: any) {
      setErrorMessage(err?.response?.data?.message || err?.message || 'Invalid verification code.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPasswordEmail = async () => {
    if (!resolvedTenant) {
      alert('Please enter your company name and continue first');
      return;
    }
    if (!forgotEmail.trim()) {
      alert('Please enter your email');
      return;
    }

    setIsLoadingForgotPassword(true);
    try {
      const response = await authApi.forgotPassword.request(resolvedTenant.tenantId, forgotEmail.trim());
      
      if (response && response.hasQuestion) {
        setPasswordQuestion(response.passwordQuestion);
        setForgotPasswordStep('question');
      } else {
        alert('Password recovery question is not set. Please contact administrator.');
      }
    } catch (error: any) {
      let errorMessage = 'Failed to retrieve password question. Please try again.';
      
      if (error?.code === 'ERR_NETWORK' || error?.code === 'ECONNREFUSED' || error?.message?.includes('Network Error')) {
        errorMessage = 'Network error. Please check:\n\n1. API server is running\n2. Check your internet connection';
      } else if (error?.response) {
        errorMessage = error.response.data?.message || `Server error (${error.response.status}). Please try again.`;
      } else if (error?.message) {
        errorMessage = error.message;
      }
      
      alert(errorMessage);
    } finally {
      setIsLoadingForgotPassword(false);
    }
  };

  const handleForgotPasswordReset = async () => {
    if (!resolvedTenant) return;
    if (!passwordAnswer.trim()) {
      alert('Please enter your password answer');
      return;
    }

    setIsLoadingForgotPassword(true);
    try {
      await authApi.forgotPassword.reset(resolvedTenant.tenantId, forgotEmail.trim(), passwordAnswer.trim());
      setForgotPasswordStep('success');
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to reset password. Please check your answer and try again.';
      alert('Error: ' + errorMessage);
    } finally {
      setIsLoadingForgotPassword(false);
    }
  };

  const closeForgotPassword = () => {
    setShowForgotPassword(false);
    setForgotPasswordStep('email');
    setForgotEmail('');
    setPasswordQuestion('');
    setPasswordAnswer('');
  };

  const needsSendCode = mfaMethod === 'Email' || mfaMethod === 'Sms';
  const showAuthenticatorSetup =
    !!mfaChallenge && needsAuthenticatorSetup(mfaChallenge, mfaMethod);
  const useSetupConfirmFlow =
    !!mfaChallenge &&
    (mfaChallenge.mfaSetupRequired || needsAuthenticatorSetup(mfaChallenge, mfaMethod));

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <div className="flex justify-center mb-4">
            <img src="/staffly-logo.png" alt="Staffly" className="h-12 w-auto" />
          </div>
          <p className="mt-2 text-sm text-gray-600">
            {step === 'company'
              ? 'Enter your company name to get started'
              : step === 'mfa'
                ? useSetupConfirmFlow
                  ? 'Set up multi-factor authentication'
                  : 'Enter your verification code'
                : resolvedTenant
                  ? `Sign in to ${resolvedTenant.tenantName}`
                  : 'Sign in to your account'}
          </p>
        </div>

        {step === 'company' ? (
          <form className="mt-8 space-y-6" onSubmit={handleCompanyLookup}>
            {errorMessage && (
              <div className="bg-red-50 border border-red-200 rounded-md p-4">
                <p className="text-sm text-red-800">{errorMessage}</p>
              </div>
            )}
            <div>
              <label htmlFor="companyName" className="block text-sm font-medium text-gray-700">
                Company Name
              </label>
              <input
                id="companyName"
                name="companyName"
                type="text"
                autoComplete="organization"
                required
                className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                placeholder="e.g. Acme Corp or your-org-code"
                value={companyName}
                onChange={(e) => {
                  setCompanyName(e.target.value);
                  if (errorMessage) setErrorMessage('');
                }}
                disabled={isLoading}
              />
              <p className="mt-1 text-xs text-gray-500">Enter your company name, code, or organization identifier</p>
            </div>
            <div>
              <button
                type="submit"
                disabled={isLoading}
                className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <span className="flex items-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Looking up...
                  </span>
                ) : (
                  'Continue'
                )}
              </button>
            </div>
          </form>
        ) : !resolvedTenant && isLoading ? (
          <div className="mt-8 flex justify-center py-8">
            <svg className="animate-spin h-8 w-8 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
          </div>
        ) : !resolvedTenant ? (
          <div className="mt-8 space-y-4">
            {errorMessage && (
              <div className="bg-red-50 border border-red-200 rounded-md p-4">
                <p className="text-sm text-red-800">{errorMessage}</p>
              </div>
            )}
          </div>
        ) : step === 'mfa' && mfaChallenge ? (
          <form className="mt-8 space-y-6" onSubmit={handleVerifyMfa}>
            <div className="flex items-center justify-between py-2 px-3 bg-blue-50 border border-blue-200 rounded-md">
              <span className="text-sm font-medium text-blue-800">{resolvedTenant.tenantName}</span>
              <button
                type="button"
                onClick={handleBackToCredentials}
                className="text-xs text-blue-600 hover:text-blue-800"
              >
                Back
              </button>
            </div>
            {errorMessage && (
              <div className="bg-red-50 border border-red-200 rounded-md p-4">
                <p className="text-sm text-red-800">{errorMessage}</p>
              </div>
            )}

            {mfaChallenge.allowedMethods.length > 1 && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Verification method</label>
                <div className="space-y-2">
                  {mfaChallenge.allowedMethods.map((method) => (
                    <label key={method} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="mfaMethod"
                        checked={mfaMethod === method}
                        onChange={() => handleMfaMethodChange(method)}
                        disabled={isLoading}
                        className="border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-sm text-gray-700">{MFA_METHOD_LABELS[method]}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
            {mfaChallenge.allowedMethods.length === 1 && (
              <p className="text-sm text-gray-600">
                Enter the code from your {MFA_METHOD_LABELS[mfaMethod]}.
              </p>
            )}

            {showAuthenticatorSetup && (
              <div className="space-y-3 border border-gray-200 rounded-md p-4 bg-white">
                <p className="text-sm text-gray-700">
                  Scan the QR code with your authenticator app (or enter the secret manually), then enter the 6-digit code.
                </p>
                {authenticatorOtpauthUrl && (
                  <div className="flex flex-col items-center gap-2 py-2">
                    <QRCodeSVG
                      value={authenticatorOtpauthUrl}
                      size={180}
                      level="M"
                      includeMargin
                      className="rounded-md bg-white"
                    />
                    <p className="text-xs text-gray-500">Scan with Google Authenticator, Microsoft Authenticator, or similar</p>
                  </div>
                )}
                {authenticatorSecret && (
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Secret (manual entry)</label>
                    <code className="block w-full text-xs break-all bg-gray-50 border border-gray-200 rounded px-2 py-2">
                      {authenticatorSecret}
                    </code>
                  </div>
                )}
                {authenticatorOtpauthUrl && (
                  <details className="text-xs text-gray-600">
                    <summary className="cursor-pointer font-medium">Show otpauth URL</summary>
                    <code className="block w-full break-all bg-gray-50 border border-gray-200 rounded px-2 py-2 mt-2">
                      {authenticatorOtpauthUrl}
                    </code>
                  </details>
                )}
              </div>
            )}

            {needsSendCode && (
              <div className="space-y-2">
                <button
                  type="button"
                  onClick={handleSendMfaCode}
                  disabled={isLoading}
                  className="w-full py-2 px-4 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                >
                  {mfaCodeSent ? 'Resend code' : `Send code via ${MFA_METHOD_LABELS[mfaMethod]}`}
                </button>
                {mfaCodeSent && (
                  <p className="text-xs text-gray-500">
                    Code sent{mfaDestination ? ` to ${mfaDestination}` : ''}.
                  </p>
                )}
              </div>
            )}

            <div>
              <label htmlFor="mfaCode" className="block text-sm font-medium text-gray-700">
                Verification code
              </label>
              <input
                id="mfaCode"
                name="mfaCode"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                required
                className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                placeholder="Enter code"
                value={mfaCode}
                onChange={(e) => {
                  setMfaCode(e.target.value);
                  if (errorMessage) setErrorMessage('');
                }}
                disabled={isLoading}
              />
            </div>

            <button
              type="submit"
              disabled={isLoading || (needsSendCode && !mfaCodeSent)}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <span className="flex items-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Verifying...
                </span>
              ) : useSetupConfirmFlow ? (
                'Confirm and sign in'
              ) : (
                'Verify and sign in'
              )}
            </button>
          </form>
        ) : (
          <form className="mt-8 space-y-6" onSubmit={handleLogin}>
            {resolvedTenant && (
              <div className={`flex items-center py-2 px-3 bg-blue-50 border border-blue-200 rounded-md ${requiresCompanyStep ? 'justify-between' : ''}`}>
                <span className="text-sm font-medium text-blue-800">{resolvedTenant.tenantName}</span>
                {requiresCompanyStep && (
                  <button
                    type="button"
                    onClick={handleBackToCompany}
                    className="text-xs text-blue-600 hover:text-blue-800"
                  >
                    Change company
                  </button>
                )}
              </div>
            )}
            {errorMessage && (
              <div className="bg-red-50 border border-red-200 rounded-md p-4">
                <p className="text-sm text-red-800">{errorMessage}</p>
              </div>
            )}
            <div className="space-y-4">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                  Email
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (errorMessage) setErrorMessage('');
                  }}
                  disabled={isLoading}
                />
              </div>
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                  Password
                </label>
                <input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    if (errorMessage) setErrorMessage('');
                  }}
                  disabled={isLoading}
                />
              </div>
            </div>
            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={rememberEmail}
                  onChange={(e) => setRememberEmail(e.target.checked)}
                  disabled={isLoading}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700">Remember my email</span>
              </label>
              <button
                type="button"
                onClick={() => setShowForgotPassword(true)}
                className="text-sm text-blue-600 hover:text-blue-500"
                disabled={isLoading}
              >
                Forgot Password?
              </button>
            </div>
            <div>
              <button
                type="submit"
                disabled={isLoading}
                className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <span className="flex items-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Signing in...
                  </span>
                ) : (
                  'Sign In'
                )}
              </button>
            </div>
          </form>
        )}
      </div>

      {/* Forgot Password Modal */}
      {showForgotPassword && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-900">Forgot Password</h3>
              <button
                onClick={closeForgotPassword}
                className="text-gray-400 hover:text-gray-600 text-2xl font-bold"
              >
                ×
              </button>
            </div>

            <div className="mt-4">
              {forgotPasswordStep === 'email' && (
                <div>
                  <p className="text-sm text-gray-600 mb-4">
                    Enter your email address to retrieve your password recovery question.
                  </p>
                  <div className="mb-4">
                    <label htmlFor="forgot-email" className="block text-sm font-medium text-gray-700">
                      Email
                    </label>
                    <input
                      id="forgot-email"
                      type="email"
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      placeholder="Enter your email"
                      value={forgotEmail}
                      onChange={(e) => setForgotEmail(e.target.value)}
                      disabled={isLoadingForgotPassword}
                    />
                  </div>
                  <button
                    onClick={handleForgotPasswordEmail}
                    disabled={isLoadingForgotPassword}
                    className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                  >
                    {isLoadingForgotPassword ? 'Loading...' : 'Continue'}
                  </button>
                </div>
              )}

              {forgotPasswordStep === 'question' && (
                <div>
                  <p className="text-sm text-gray-600 mb-4">
                    Please answer your security question to reset your password.
                  </p>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Security Question
                    </label>
                    <div className="p-3 bg-gray-50 border border-gray-300 rounded-md">
                      <p className="text-sm text-gray-900">{passwordQuestion}</p>
                    </div>
                  </div>
                  <div className="mb-4">
                    <label htmlFor="password-answer" className="block text-sm font-medium text-gray-700">
                      Answer
                    </label>
                    <input
                      id="password-answer"
                      type="text"
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      placeholder="Enter your answer"
                      value={passwordAnswer}
                      onChange={(e) => setPasswordAnswer(e.target.value)}
                      disabled={isLoadingForgotPassword}
                    />
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        setForgotPasswordStep('email');
                        setPasswordAnswer('');
                      }}
                      disabled={isLoadingForgotPassword}
                      className="flex-1 py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      Back
                    </button>
                    <button
                      onClick={handleForgotPasswordReset}
                      disabled={isLoadingForgotPassword}
                      className="flex-1 py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                    >
                      {isLoadingForgotPassword ? 'Loading...' : 'Reset Password'}
                    </button>
                  </div>
                </div>
              )}

              {forgotPasswordStep === 'success' && (
                <div className="text-center">
                  <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
                    <svg className="h-6 w-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Password Reset Successful!</h3>
                  <p className="text-sm text-gray-600 mb-4">
                    A temporary password has been sent to your email address ({forgotEmail}).
                    {'\n\n'}Please check your inbox and use the temporary password to sign in.
                  </p>
                  <button
                    onClick={closeForgotPassword}
                    className="w-full py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    Close
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Login;
